<script lang="ts" setup>
	import useAccountApi from '~/services/apis/account'
	import { usePlansApi } from '~/services/apis/plan'

	const { productCategoryType } = useUtils()

	const props = defineProps<{
		planDetails: PlanInformationContext | null
		categoryName: string
		totalCustodian: number
		totalDependant: number
		status: string
	}>()
	defineEmits<{
		(event: 'deactivate'): void
		(event: 'view-policy'): void
	}>()
	const { plan_id } = useRoute().params
	const { confirmPasswordApi } = useAccountApi()
	const { replace } = useRouter()
	const formData = reactive({
		password: '',
		reasons: [],
		other_reason: '',
		selectReason: false
	})
	const isDeactivateDialog = shallowRef(false)
	const isDeactivating = shallowRef(false)
	const { deactivatePlan } = usePlansApi()
	const reasons = ref([
		'I have a duplicate plan',
		'I want to get a similar plan',
		'I am not satisfied with this plan',
		"I couldn't make claim"
	])
	const planName = computed(() => {
		return props.planDetails?.product?.name
	})

	const canNotDeactivate = computed(() => {
		return props.planDetails?.is_deactivated
	})
	const categoryType = computed(() => {
		return productCategoryType(props.categoryName, props.planDetails?.product?.code_name ?? '')
	})
	const categoryPlanLabel = computed(() => {
		const category = categoryType.value
		return category?.isHealth || category?.isTravel
			? 'Staff'
			: category?.isAuto
				? 'Vehicle(s)'
				: String(props.planDetails?.product?.productCategory?.name ?? 'N/A')
	})
	const verifyPassword = async () => {
		if (!formData.password) {
			return null
		}
		try {
			isDeactivating.value = true
			const passResult = await confirmPasswordApi({
				password: formData.password
			})
			if (passResult?.data) {
				formData.selectReason = !!formData.password
			}
		} finally {
			isDeactivating.value = false
		}
	}
	const deactivate = async () => {
		if (formData.selectReason) {
			try {
				isDeactivating.value = true
				const res = await deactivatePlan(plan_id as string, {}, { is_immediate: true })
				if (res?.data) {
					useNotification().alert({
						status: res.responseCode,
						message: res.responseText
					})
					resolve(() =>
						useMixpanel().track('deactivate_plan_biz_successful', {
							plan_name: planName.value
						})
					)
					replace('/mycover')
				} else {
					resolve(() =>
						useMixpanel().track('deactivate_plan_biz_failed', {
							plan_name: planName.value,
							plan_status: 'active'
						})
					)
				}
			} finally {
				isDeactivating.value = false
			}
		}
	}
</script>

<template>
	<div class="relative flex min-h-[419px] w-full flex-col items-center rounded border border-gray-200 pb-10">
		<div class="flex h-[260px] w-full flex-col bg-purple-50 p-5 sm:p-8">
			<div class="flex items-center justify-between">
				<h3 class="text-xl font-semibold capitalize text-blue-950">
					{{ planName }}
				</h3>
				<div class="flex items-center gap-x-2">
					<lazy-tools-icon
						:dir="!canNotDeactivate ? 'fill' : 'line'"
						:icon="!canNotDeactivate ? 'check-2' : 'cancel'"
						:path-class="canNotDeactivate ? 'stroke-error-500' : ''"
					/>
					<span class="font-medium" :class="[canNotDeactivate ? 'text-error-500' : 'text-gray-500']">
						{{ status }}
					</span>
				</div>
			</div>

			<div class="mt-auto font-medium leading-5 tracking-wide text-gray-600">
				<p v-show="categoryType?.isGadget || categoryType?.isAuto" class="mb-6">
					This a yearly {{ categoryType?.isGadget ? 'gadget' : 'vehicle' }} plan made to cover all your
					business {{ categoryType?.isGadget ? 'gadgets' : 'vehicles' }}.
				</p>
				<p v-show="categoryType.isHealth" class="mb-6">
					<span class="capitalize">{{ planName }}</span> is a subscription-based health plan that meets all
					your healthcare needs.
				</p>
				<p>
					It currently covers
					<span role="button" class="font-medium text-primary-2 underline" @click="$emit('view-policy')">
						{{ totalCustodian }} {{ categoryPlanLabel }}
					</span>

					<span
						v-show="categoryType.isHealth"
						role="button"
						class="font-medium text-primary-2 underline"
						@click="$emit('view-policy')"
					>
						and {{ totalDependant }} dependants
					</span>
				</p>
			</div>
		</div>
		<div class="mt-auto w-full px-5 sm:px-8">
			<button
				v-if="!canNotDeactivate"
				aria-describedby="deactivation"
				type="button"
				data-testid="deactivate plan"
				class="default-btn w-full justify-center !border-0 bg-gray-200 tracking-wide text-gray-500"
				@click="() => (isDeactivateDialog = true)"
			>
				Deactivate Plan
			</button>
		</div>

		<dialogs-error v-model:show="isDeactivateDialog" header="Deactivate Plan" type="warning">
			<template #header>
				<div
					v-if="!formData.selectReason"
					class="flex w-full flex-col items-center justify-center bg-gray-50 p-8"
				>
					<shared-header title="Deactivate Plan" title-class="text-error-500" />

					<p class="text-base font-medium leading-loose tracking-wide text-gray-500">
						Note that your business will no longer be insured by
						<span class="font-medium capitalize text-gray-600">{{ planName }}</span> if plan is deactivated.
						All policies tied to this plan will also be deactivated.
						<br />
						Do you still want to deactivate this plan?
					</p>
				</div>
				<shared-header
					v-else
					title="Deactivate Plan"
					sub-title="Kindly let us know why you're deactivating plan"
					title-class="text-error-500"
					class="w-full"
				/>
			</template>

			<template #icon>
				<lazy-tools-icon icon="trash" :height="32" :width="32" dir="other" path-class="stroke-error-500" />
			</template>

			<template #p>
				<a-form :model="formData" class="!mt-12 text-left" @finish="deactivate">
					<template v-if="formData.selectReason">
						<div class="mb-5 w-full rounded bg-purple-50 p-8">
							<a-form-item name="reasons" :required="!formData.other_reason">
								<a-checkbox-group
									v-model:value="formData.reasons"
									:options="reasons"
									class="flex flex-col gap-y-5"
								>
									<template #label="{ label }">
										<span class="font-medium tracking-wide text-gray-500">{{ label }}</span>
									</template>
								</a-checkbox-group>
							</a-form-item>
						</div>

						<a-form-item label="Others" name="other_reason" :rules="[{ max: 200 }]">
							<a-textarea
								v-model:value="formData.other_reason"
								auto-size
								class="custom-input !min-h-[90px] w-full !bg-gray-50 !px-5 !pt-3"
								placeholder="Kindly state any other reason you have"
							/>
						</a-form-item>
					</template>

					<a-form-item v-if="!formData.selectReason" label="Password" required name="password">
						<a-input-password
							v-model:value="formData.password"
							autocomplete="new password"
							class="custom-input !bg-gray-5 w-full !px-5"
							placeholder="Enter your password"
						/>
					</a-form-item>

					<div class="mt-8 flex items-center justify-between gap-x-8">
						<button
							type="button"
							class="default-btn w-full justify-center bg-gray-200"
							name="back"
							@click="() => (formData.selectReason = false)"
						>
							Back
						</button>

						<a-button
							v-if="!formData.selectReason"
							:loading="isDeactivating"
							class="default-btn deactivate-btn"
							name="verify password"
							aria-labelledby="Verify password"
							@click="verifyPassword"
						>
							Continue
						</a-button>
						<a-button
							v-else
							html-type="submit"
							:loading="isDeactivating"
							:disabled="isDeactivating"
							class="default-btn deactivate-btn"
							name="deactivate"
						>
							Deactivate
						</a-button>
					</div>
				</a-form>
			</template>
			<template #button>
				<span />
			</template>
		</dialogs-error>
	</div>
</template>

<style scoped>
	.deactivate-btn {
		@apply w-full justify-center !border-error-500 !bg-error-500 font-medium !text-white hover:!bg-error-600 disabled:!bg-error-300;
	}
</style>
